import { useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Loader2, ImagePlus } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api, get, errMsg } from "@/lib/api";
import { SkeletonRows } from "@/components/app/Common";

const FIELDS = [["company_name", "Company name"], ["support_email", "Support email"]];

export default function BrandingSettings() {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({ queryKey: ["settings-branding"], queryFn: () => get("/settings/branding") });
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (data) setForm(data); }, [data]);

  const onLogo = (file) => {
    if (!file) return;
    if (file.size > 500 * 1024) return toast.error("Logo must be under 500 KB");
    const reader = new FileReader();
    reader.onload = () => setForm((f) => ({ ...f, logo_data_url: reader.result }));
    reader.readAsDataURL(file);
  };

  const save = async () => {
    setBusy(true);
    try {
      await api.put("/settings/branding", form);
      qc.invalidateQueries({ queryKey: ["settings-branding"] });
      qc.invalidateQueries({ queryKey: ["public-branding"] });
      toast.success("Branding saved");
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  if (isLoading || !form) return <SkeletonRows rows={5} />;
  return (
    <section className="panel space-y-5 p-6" data-testid="branding-settings">
      <h2 className="text-lg font-semibold">Branding</h2>
      <div className="grid gap-4 md:grid-cols-2">
        {FIELDS.map(([k, label]) => (
          <div key={k} className="space-y-2">
            <Label htmlFor={k}>{label}</Label>
            <Input id={k} value={form[k] || ""} onChange={(e) => setForm({ ...form, [k]: e.target.value })} data-testid={`branding-${k}-input`} />
          </div>
        ))}
        <div className="space-y-2">
          <Label htmlFor="primary_color">Primary colour</Label>
          <div className="flex items-center gap-2">
            <input type="color" value={form.primary_color} onChange={(e) => setForm({ ...form, primary_color: e.target.value.toUpperCase() })} className="h-9 w-12 cursor-pointer rounded-lg border border-line" data-tour="pt-branding-color-picker" data-testid="branding-color-picker" />
            <Input id="primary_color" value={form.primary_color} onChange={(e) => setForm({ ...form, primary_color: e.target.value })} className="w-32" data-testid="branding-primary_color-input" />
          </div>
        </div>
      </div>
      <div className="space-y-2">
        <Label>Logo</Label>
        <div className="flex items-center gap-4">
          <div className="flex h-16 w-16 items-center justify-center overflow-hidden rounded-lg border border-line bg-subtle" data-testid="branding-logo-preview">
            {form.logo_data_url ? <img src={form.logo_data_url} alt="Logo" className="h-full w-full object-contain" /> : <ImagePlus size={20} className="text-ink-muted" />}
          </div>
          <label className="cursor-pointer text-sm text-brand hover:underline">
            Upload logo
            <input type="file" accept="image/*" className="hidden" onChange={(e) => onLogo(e.target.files?.[0])} data-tour="pt-branding-logo-input" data-testid="branding-logo-input" />
          </label>
          {form.logo_data_url && <button className="text-sm text-ink-muted hover:text-risk" onClick={() => setForm({ ...form, logo_data_url: null })} data-testid="branding-logo-remove">Remove</button>}
        </div>
      </div>
      <div className="flex justify-end">
        <Button onClick={save} disabled={busy} data-tour="pt-branding-save-btn" data-testid="branding-save-btn">{busy && <Loader2 size={16} className="animate-spin" />} Save branding</Button>
      </div>
    </section>
  );
}
